import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend
} from 'recharts';
import {
  DollarSign, Package, Tag, TrendingUp, TrendingDown, Users, Download,
  ChevronDown, ShoppingCart, CreditCard, Clock, Calendar, Layers,
  ArrowUpRight, ArrowDownRight, Wallet, Sparkles
} from 'lucide-react';

import { getBackendUrl } from '../../utils/api';

const API = `${getBackendUrl()}/api`;

const brl = (v) =>
  `R$ ${Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const brlShort = (v) => {
  const n = Number(v || 0);
  if (n >= 1000) return `R$ ${(n / 1000).toFixed(1)}k`;
  return `R$ ${n.toFixed(0)}`;
};

// Paletas
const PAYMENT_COLORS = {
  'Pix': '#22c55e',
  'Pix Automático': '#14b8a6',
  'Dinheiro': '#f59e0b',
  'Cartão': '#3b82f6',
  'Cartão de Crédito': '#6366f1',
  'Cartão de Débito': '#0ea5e9',
  'Não informado': '#6b7280',
};
const SOURCE_COLORS = {
  'Cardápio Digital': '#3b82f6',
  'IA WhatsApp': '#22c55e',
  'Escuta Passiva': '#a855f7',
  'Mesa/Garçom': '#f59e0b',
  'PDV/Balcão': '#ec4899',
};
const FALLBACK_COLORS = ['#22c55e', '#3b82f6', '#f59e0b', '#a855f7', '#ec4899', '#14b8a6', '#ef4444', '#6366f1'];

const colorFor = (map, key, i) => map[key] || FALLBACK_COLORS[i % FALLBACK_COLORS.length];

// Tooltip customizado (tema escuro)
const DarkTooltip = ({ active, payload, label, money = true }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-gray-900/95 border border-white/10 rounded-lg px-3 py-2 shadow-xl backdrop-blur">
      {label !== undefined && <p className="text-xs text-gray-300 mb-1 font-medium">{label}</p>}
      {payload.map((p, i) => (
        <p key={i} className="text-xs" style={{ color: p.color || p.fill }}>
          {p.name}: <span className="font-semibold text-white">{money && p.dataKey === 'revenue' ? brl(p.value) : p.value}</span>
        </p>
      ))}
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, growth, gradient, iconColor }) => (
  <div className={`bg-gradient-to-br ${gradient} border rounded-2xl p-4 md:p-5 relative overflow-hidden`}>
    <div className="flex items-center gap-2 mb-2">
      <Icon className={`w-4 h-4 md:w-5 md:h-5 ${iconColor} flex-shrink-0`} />
      <p className="text-[10px] md:text-xs text-gray-300 uppercase font-semibold tracking-wide truncate">{label}</p>
    </div>
    <p className="text-xl md:text-3xl font-bold text-white truncate">{value}</p>
    {growth !== null && growth !== undefined && (
      <div className={`inline-flex items-center gap-1 mt-2 text-[11px] md:text-xs font-semibold px-2 py-0.5 rounded-full ${
        growth >= 0 ? 'bg-green-500/20 text-green-300' : 'bg-red-500/20 text-red-300'
      }`}>
        {growth >= 0 ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
        {Math.abs(growth).toFixed(1)}%
        <span className="text-gray-400 font-normal hidden sm:inline">vs. período anterior</span>
      </div>
    )}
  </div>
);

const SectionCard = ({ title, subtitle, icon: Icon, children, action }) => (
  <div className="glass-card p-4 md:p-5">
    <div className="flex items-center justify-between mb-4 gap-2">
      <div className="flex items-center gap-2 min-w-0">
        {Icon && <Icon className="w-4 h-4 md:w-5 md:h-5 text-gray-300 flex-shrink-0" />}
        <div className="min-w-0">
          <h3 className="text-sm md:text-base font-bold text-white truncate">{title}</h3>
          {subtitle && <p className="text-[11px] md:text-xs text-gray-400 truncate">{subtitle}</p>}
        </div>
      </div>
      {action}
    </div>
    {children}
  </div>
);

const EmptyMini = ({ text }) => (
  <div className="text-center py-8">
    <Layers className="w-8 h-8 text-gray-600 mx-auto mb-2" />
    <p className="text-xs text-gray-500">{text}</p>
  </div>
);

const ReportsContent = () => {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [preset, setPreset] = useState('30d');
  const todayStr = () => new Date().toISOString().split('T')[0];
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().setDate(new Date().getDate() - 29)).toISOString().split('T')[0],
    end: todayStr(),
  });

  const [overview, setOverview] = useState(null);
  const [productsData, setProductsData] = useState(null);
  const [categoriesData, setCategoriesData] = useState(null);
  const [monthlyData, setMonthlyData] = useState(null);
  const [waitersData, setWaitersData] = useState(null);
  const [waiterFilter, setWaiterFilter] = useState('all');
  const [expandedWaiter, setExpandedWaiter] = useState(null);

  const granularity = dateRange.start === dateRange.end ? 'hour' : 'day';

  const applyPreset = (id) => {
    const now = new Date();
    let start, end = todayStr();
    if (id === 'today') {
      start = todayStr();
    } else if (id === 'yesterday') {
      const y = new Date(); y.setDate(y.getDate() - 1);
      start = y.toISOString().split('T')[0];
      end = start;
    } else if (id === '7d') {
      const s = new Date(); s.setDate(s.getDate() - 6);
      start = s.toISOString().split('T')[0];
    } else if (id === '30d') {
      const s = new Date(); s.setDate(s.getDate() - 29);
      start = s.toISOString().split('T')[0];
    } else if (id === 'month') {
      start = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
    }
    setPreset(id);
    setDateRange({ start, end });
    setShowDatePicker(false);
  };

  const fetchOverview = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/reports/overview`, {
        params: { start_date: dateRange.start, end_date: dateRange.end, granularity },
      });
      setOverview(res.data);
      // Evolução mensal (6 meses) — carregado junto p/ a Visão Geral
      try {
        const m = await axios.get(`${API}/reports/monthly-comparison`, { params: { months: 6 } });
        setMonthlyData(m.data);
      } catch (e) { /* opcional */ }
    } catch (e) {
      console.error('Erro overview:', e);
      setOverview(null);
    }
    setLoading(false);
  }, [dateRange, granularity]);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const [p, c] = await Promise.all([
        axios.get(`${API}/reports/top-products`, { params: { start_date: dateRange.start, end_date: dateRange.end, limit: 10 } }),
        axios.get(`${API}/reports/by-category`, { params: { start_date: dateRange.start, end_date: dateRange.end } }),
      ]);
      setProductsData(p.data);
      setCategoriesData(c.data);
    } catch (e) { console.error('Erro produtos:', e); }
    setLoading(false);
  }, [dateRange]);

  const fetchWaiters = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/reports/by-waiter`, {
        params: { start_date: dateRange.start, end_date: dateRange.end },
      });
      setWaitersData(res.data);
    } catch (e) { console.error('Erro atendentes:', e); }
    setLoading(false);
  }, [dateRange]);

  useEffect(() => {
    if (activeTab === 'overview') fetchOverview();
    else if (activeTab === 'products') fetchProducts();
    else if (activeTab === 'team') fetchWaiters();
  }, [activeTab, fetchOverview, fetchProducts, fetchWaiters]);

  const exportToCSV = (data, filename) => {
    if (!data || data.length === 0) return;
    const headers = Object.keys(data[0]).join(',');
    const rows = data.map((row) => Object.values(row).join(','));
    const csv = [headers, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}_${dateRange.start}_${dateRange.end}.csv`;
    a.click();
  };
  const exportToExcel = (data, filename, sheetName = 'Relatório') => {
    if (!data || data.length === 0) return;
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    XLSX.writeFile(workbook, `${filename}_${dateRange.start}_${dateRange.end}.xlsx`);
  };

  const presets = [
    { id: 'today', label: 'Hoje' },
    { id: 'yesterday', label: 'Ontem' },
    { id: '7d', label: '7 dias' },
    { id: '30d', label: '30 dias' },
    { id: 'month', label: 'Este mês' },
  ];

  const tabs = [
    { id: 'overview', label: 'Visão Geral', icon: TrendingUp },
    { id: 'products', label: 'Produtos', icon: Package },
    { id: 'team', label: 'Equipe', icon: Users },
  ];

  const cmp = overview?.comparison || {};
  const sum = overview?.summary || {};

  return (
    <div className="space-y-4 md:space-y-6" data-testid="reports-content">
      {/* Header + Filtro de período */}
      <div className="glass-card p-4 md:p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg flex-shrink-0">
            <Sparkles className="w-5 h-5 md:w-6 md:h-6 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl md:text-3xl font-bold text-white truncate">Relatórios</h1>
            <p className="text-xs md:text-sm text-gray-400 hidden sm:block">Tudo que acontece na sua loja, em um só lugar</p>
          </div>
        </div>

        {/* Presets rápidos (scroll horizontal no mobile) */}
        <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide pb-1">
          {presets.map((p) => (
            <button
              key={p.id}
              onClick={() => applyPreset(p.id)}
              data-testid={`preset-${p.id}`}
              className={`px-3 py-1.5 rounded-full text-xs md:text-sm font-medium whitespace-nowrap flex-shrink-0 transition-all ${
                preset === p.id
                  ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-lg'
                  : 'bg-white/5 text-gray-300 hover:bg-white/10'
              }`}
            >
              {p.label}
            </button>
          ))}
          <button
            onClick={() => setShowDatePicker(!showDatePicker)}
            data-testid="preset-custom"
            className={`px-3 py-1.5 rounded-full text-xs md:text-sm font-medium whitespace-nowrap flex-shrink-0 flex items-center gap-1 transition-all ${
              preset === 'custom' ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-lg' : 'bg-white/5 text-gray-300 hover:bg-white/10'
            }`}
          >
            <Calendar size={14} /> Personalizado
            <ChevronDown className={`w-3 h-3 transition-transform ${showDatePicker ? 'rotate-180' : ''}`} />
          </button>
        </div>

        {/* Intervalo atual */}
        <p className="text-[11px] md:text-xs text-gray-500 mt-2">
          {new Date(dateRange.start + 'T12:00').toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' })}
          {dateRange.start !== dateRange.end && ` — ${new Date(dateRange.end + 'T12:00').toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' })}`}
        </p>

        {showDatePicker && (
          <div className="mt-3 grid grid-cols-2 gap-2 animate-in fade-in slide-in-from-top-2">
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">Início</label>
              <input
                type="date" value={dateRange.start}
                onChange={(e) => { setPreset('custom'); setDateRange({ ...dateRange, start: e.target.value }); }}
                className="glass-input w-full text-sm py-2"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">Fim</label>
              <input
                type="date" value={dateRange.end}
                onChange={(e) => { setPreset('custom'); setDateRange({ ...dateRange, end: e.target.value }); }}
                className="glass-input w-full text-sm py-2"
              />
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="glass-card overflow-hidden">
        <div className="flex overflow-x-auto scrollbar-hide p-2 gap-2 border-b border-white/10">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              data-testid={`reports-tab-${t.id}`}
              className={`flex items-center gap-2 px-3 md:px-4 py-2 rounded-lg transition-all whitespace-nowrap flex-shrink-0 ${
                activeTab === t.id
                  ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-lg'
                  : 'text-gray-400 hover:text-white hover:bg-white/5'
              }`}
            >
              <t.icon size={16} />
              <span className="text-xs md:text-sm font-medium">{t.label}</span>
            </button>
          ))}
        </div>

        <div className="p-3 md:p-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mb-3"></div>
              <p className="text-sm text-gray-400">Carregando...</p>
            </div>
          ) : (
            <>
              {/* ============ VISÃO GERAL ============ */}
              {activeTab === 'overview' && overview && (
                <div className="space-y-4 md:space-y-6">
                  {/* KPIs */}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <StatCard icon={DollarSign} label="Receita" value={brl(sum.total_revenue)} growth={cmp.revenue_growth}
                      gradient="from-green-500/20 to-emerald-500/10 border-green-500/30" iconColor="text-green-400" />
                    <StatCard icon={ShoppingCart} label="Pedidos" value={sum.total_orders || 0} growth={cmp.orders_growth}
                      gradient="from-blue-500/20 to-cyan-500/10 border-blue-500/30" iconColor="text-blue-400" />
                    <StatCard icon={TrendingUp} label="Ticket Médio" value={brl(sum.average_ticket)} growth={cmp.ticket_growth}
                      gradient="from-purple-500/20 to-pink-500/10 border-purple-500/30" iconColor="text-purple-400" />
                    <StatCard icon={Package} label="Itens Vendidos" value={sum.total_items || 0} growth={null}
                      gradient="from-orange-500/20 to-amber-500/10 border-orange-500/30" iconColor="text-orange-400" />
                  </div>

                  {/* Série temporal */}
                  <SectionCard
                    title={granularity === 'hour' ? 'Vendas por hora' : 'Evolução das vendas'}
                    subtitle="Receita e nº de pedidos no período"
                    icon={TrendingUp}
                  >
                    {overview.timeseries && overview.timeseries.some((d) => d.orders > 0) ? (
                      <ResponsiveContainer width="100%" height={280}>
                        <AreaChart data={overview.timeseries} margin={{ top: 5, right: 8, left: -12, bottom: 0 }}>
                          <defs>
                            <linearGradient id="gRev" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#22c55e" stopOpacity={0.5} />
                              <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                            </linearGradient>
                            <linearGradient id="gOrd" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.4} />
                              <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#ffffff12" vertical={false} />
                          <XAxis dataKey={granularity === 'hour' ? 'label' : 'label_short'} stroke="#9ca3af" fontSize={11} tickLine={false} axisLine={false} interval="preserveStartEnd" minTickGap={20} />
                          <YAxis yAxisId="left" stroke="#22c55e" fontSize={11} tickLine={false} axisLine={false} tickFormatter={brlShort} width={52} />
                          <YAxis yAxisId="right" orientation="right" stroke="#3b82f6" fontSize={11} tickLine={false} axisLine={false} width={28} />
                          <Tooltip content={<DarkTooltip />} />
                          <Legend wrapperStyle={{ fontSize: 12 }} />
                          <Area yAxisId="left" type="monotone" dataKey="revenue" name="Receita" stroke="#22c55e" strokeWidth={2.5} fill="url(#gRev)" />
                          <Area yAxisId="right" type="monotone" dataKey="orders" name="Pedidos" stroke="#3b82f6" strokeWidth={2} fill="url(#gOrd)" />
                        </AreaChart>
                      </ResponsiveContainer>
                    ) : <EmptyMini text="Sem vendas neste período" />}
                  </SectionCard>

                  {/* Pagamentos + Canais */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <SectionCard title="Como seus clientes pagam" subtitle="Receita por meio de pagamento" icon={Wallet}>
                      {overview.by_payment_method && overview.by_payment_method.length > 0 ? (
                        <div className="flex flex-col sm:flex-row items-center gap-4">
                          <ResponsiveContainer width="100%" height={200} className="max-w-[220px]">
                            <PieChart>
                              <Pie data={overview.by_payment_method} dataKey="revenue" nameKey="method" cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={2}>
                                {overview.by_payment_method.map((e, i) => (
                                  <Cell key={i} fill={colorFor(PAYMENT_COLORS, e.method, i)} stroke="#0000" />
                                ))}
                              </Pie>
                              <Tooltip content={<DarkTooltip />} />
                            </PieChart>
                          </ResponsiveContainer>
                          <div className="flex-1 w-full space-y-2">
                            {overview.by_payment_method.map((e, i) => (
                              <div key={i} className="flex items-center justify-between gap-2">
                                <div className="flex items-center gap-2 min-w-0">
                                  <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: colorFor(PAYMENT_COLORS, e.method, i) }} />
                                  <span className="text-sm text-gray-300 truncate">{e.method}</span>
                                  <span className="text-[10px] text-gray-500">({e.count})</span>
                                </div>
                                <span className="text-sm font-semibold text-white flex-shrink-0">{brl(e.revenue)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : <EmptyMini text="Sem dados de pagamento" />}
                    </SectionCard>

                    <SectionCard title="De onde vêm seus pedidos" subtitle="Receita por canal de venda" icon={Layers}>
                      {overview.by_source && overview.by_source.length > 0 ? (
                        <ResponsiveContainer width="100%" height={Math.max(200, overview.by_source.length * 46)}>
                          <BarChart data={overview.by_source} layout="vertical" margin={{ top: 0, right: 16, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#ffffff12" horizontal={false} />
                            <XAxis type="number" stroke="#9ca3af" fontSize={11} tickLine={false} axisLine={false} tickFormatter={brlShort} />
                            <YAxis type="category" dataKey="source" stroke="#9ca3af" fontSize={11} tickLine={false} axisLine={false} width={92} />
                            <Tooltip content={<DarkTooltip />} cursor={{ fill: '#ffffff08' }} />
                            <Bar dataKey="revenue" name="Receita" radius={[0, 6, 6, 0]}>
                              {overview.by_source.map((e, i) => (
                                <Cell key={i} fill={colorFor(SOURCE_COLORS, e.source, i)} />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      ) : <EmptyMini text="Sem dados de canal" />}
                    </SectionCard>
                  </div>

                  {/* Horários de pico + Dias da semana */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <SectionCard title="Horários de pico" subtitle="Receita por hora do dia" icon={Clock}>
                      {overview.peak_hours && overview.peak_hours.some((h) => h.orders > 0) ? (
                        <ResponsiveContainer width="100%" height={220}>
                          <BarChart data={overview.peak_hours} margin={{ top: 5, right: 8, left: -12, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#ffffff12" vertical={false} />
                            <XAxis dataKey="hour" stroke="#9ca3af" fontSize={10} tickLine={false} axisLine={false} interval={2} />
                            <YAxis stroke="#9ca3af" fontSize={11} tickLine={false} axisLine={false} tickFormatter={brlShort} width={52} />
                            <Tooltip content={<DarkTooltip />} cursor={{ fill: '#ffffff08' }} />
                            <Bar dataKey="revenue" name="Receita" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      ) : <EmptyMini text="Sem vendas neste período" />}
                    </SectionCard>

                    <SectionCard title="Dias da semana" subtitle="Receita por dia da semana" icon={Calendar}>
                      {overview.by_weekday && overview.by_weekday.some((d) => d.orders > 0) ? (
                        <ResponsiveContainer width="100%" height={220}>
                          <BarChart data={overview.by_weekday} margin={{ top: 5, right: 8, left: -12, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#ffffff12" vertical={false} />
                            <XAxis dataKey="day" stroke="#9ca3af" fontSize={11} tickLine={false} axisLine={false} />
                            <YAxis stroke="#9ca3af" fontSize={11} tickLine={false} axisLine={false} tickFormatter={brlShort} width={52} />
                            <Tooltip content={<DarkTooltip />} cursor={{ fill: '#ffffff08' }} />
                            <Bar dataKey="revenue" name="Receita" fill="#a855f7" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      ) : <EmptyMini text="Sem vendas neste período" />}
                    </SectionCard>
                  </div>

                  {/* Evolução mensal */}
                  {monthlyData && monthlyData.length > 0 && (
                    <SectionCard title="Evolução mensal" subtitle="Últimos meses" icon={TrendingUp}
                      action={
                        <button onClick={() => exportToExcel(monthlyData, 'comparativo_mensal', 'Mensal')}
                          className="glass-button-primary flex items-center gap-1.5 text-xs py-1.5 px-3">
                          <Download size={14} /> Excel
                        </button>
                      }>
                      <ResponsiveContainer width="100%" height={240}>
                        <BarChart data={[...monthlyData].reverse()} margin={{ top: 5, right: 8, left: -12, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#ffffff12" vertical={false} />
                          <XAxis dataKey="month" stroke="#9ca3af" fontSize={11} tickLine={false} axisLine={false} />
                          <YAxis stroke="#9ca3af" fontSize={11} tickLine={false} axisLine={false} tickFormatter={brlShort} width={52} />
                          <Tooltip content={<DarkTooltip />} cursor={{ fill: '#ffffff08' }} />
                          <Bar dataKey="revenue" name="Receita" fill="#6366f1" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </SectionCard>
                  )}

                  {/* Tipo de entrega (compacto) */}
                  {overview.by_delivery_type && overview.by_delivery_type.length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {overview.by_delivery_type.map((d, i) => (
                        <div key={i} className="bg-white/5 rounded-xl p-3 border border-white/5">
                          <p className="text-xs text-gray-400 capitalize truncate">{d.type}</p>
                          <p className="text-lg font-bold text-white">{d.count}</p>
                          <p className="text-sm text-green-400 font-semibold">{brl(d.revenue)}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* ============ PRODUTOS ============ */}
              {activeTab === 'products' && (
                <div className="space-y-4 md:space-y-6">
                  <SectionCard title="Produtos mais vendidos" subtitle="Top 10 por receita" icon={Package}
                    action={
                      productsData && productsData.length > 0 && (
                        <button onClick={() => exportToExcel(productsData, 'produtos_mais_vendidos', 'Top Produtos')}
                          className="glass-button-primary flex items-center gap-1.5 text-xs py-1.5 px-3">
                          <Download size={14} /> Excel
                        </button>
                      )
                    }>
                    {productsData && productsData.length > 0 ? (
                      <>
                        <ResponsiveContainer width="100%" height={Math.max(220, productsData.length * 38)}>
                          <BarChart data={productsData} layout="vertical" margin={{ top: 0, right: 16, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#ffffff12" horizontal={false} />
                            <XAxis type="number" stroke="#9ca3af" fontSize={11} tickLine={false} axisLine={false} tickFormatter={brlShort} />
                            <YAxis type="category" dataKey="product_name" stroke="#9ca3af" fontSize={11} tickLine={false} axisLine={false} width={120} />
                            <Tooltip content={<DarkTooltip />} cursor={{ fill: '#ffffff08' }} />
                            <Bar dataKey="revenue" name="Receita" fill="#22c55e" radius={[0, 6, 6, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                        <div className="space-y-2 mt-4">
                          {productsData.map((product, index) => (
                            <div key={index} className="flex items-center justify-between bg-white/5 rounded-lg p-3">
                              <div className="flex items-center gap-3 min-w-0">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white text-sm flex-shrink-0 ${
                                  index === 0 ? 'bg-gradient-to-br from-yellow-400 to-orange-500' :
                                  index === 1 ? 'bg-gradient-to-br from-gray-300 to-gray-400' :
                                  index === 2 ? 'bg-gradient-to-br from-orange-600 to-red-600' :
                                  'bg-gradient-to-br from-blue-500 to-cyan-500'}`}>
                                  {index + 1}
                                </div>
                                <div className="min-w-0">
                                  <p className="text-white font-medium text-sm truncate">{product.product_name}</p>
                                  <p className="text-xs text-gray-400">{product.quantity} un.</p>
                                </div>
                              </div>
                              <p className="text-base font-bold text-green-400 flex-shrink-0">{brl(product.revenue)}</p>
                            </div>
                          ))}
                        </div>
                      </>
                    ) : <EmptyMini text="Nenhum produto vendido no período" />}
                  </SectionCard>

                  <SectionCard title="Vendas por categoria" subtitle="Receita agrupada por categoria" icon={Tag}
                    action={
                      categoriesData && categoriesData.length > 0 && (
                        <button onClick={() => exportToExcel(categoriesData, 'vendas_por_categoria', 'Categorias')}
                          className="glass-button-primary flex items-center gap-1.5 text-xs py-1.5 px-3">
                          <Download size={14} /> Excel
                        </button>
                      )
                    }>
                    {categoriesData && categoriesData.length > 0 ? (
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-center">
                        <ResponsiveContainer width="100%" height={240} className="max-w-[260px] mx-auto">
                          <PieChart>
                            <Pie data={categoriesData} dataKey="revenue" nameKey="category_name" cx="50%" cy="50%" outerRadius={90} paddingAngle={2}>
                              {categoriesData.map((e, i) => (<Cell key={i} fill={FALLBACK_COLORS[i % FALLBACK_COLORS.length]} stroke="#0000" />))}
                            </Pie>
                            <Tooltip content={<DarkTooltip />} />
                          </PieChart>
                        </ResponsiveContainer>
                        <div className="space-y-2">
                          {categoriesData.map((c, i) => (
                            <div key={i} className="flex items-center justify-between gap-2 bg-white/5 rounded-lg p-3">
                              <div className="flex items-center gap-2 min-w-0">
                                <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: FALLBACK_COLORS[i % FALLBACK_COLORS.length] }} />
                                <div className="min-w-0">
                                  <p className="text-sm text-white truncate">{c.category_name}</p>
                                  <p className="text-xs text-gray-400">{c.total_quantity} un. · ticket {brl(c.average_price)}</p>
                                </div>
                              </div>
                              <span className="text-sm font-semibold text-green-400 flex-shrink-0">{brl(c.revenue)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : <EmptyMini text="Nenhuma categoria com vendas no período" />}
                  </SectionCard>
                </div>
              )}

              {/* ============ EQUIPE (ATENDENTES) ============ */}
              {activeTab === 'team' && waitersData && (waitersData.waiters || []).length > 0 && (
                <div>
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4">
                    <div className="grid grid-cols-3 gap-3 flex-1">
                      <div className="glass-card p-3">
                        <p className="text-xs text-gray-400">Atendentes</p>
                        <p className="text-lg md:text-xl font-bold text-white" data-testid="waiters-count">{waitersData.totals?.waiters_count || 0}</p>
                      </div>
                      <div className="glass-card p-3">
                        <p className="text-xs text-gray-400">Mesas fechadas</p>
                        <p className="text-lg md:text-xl font-bold text-white">{waitersData.totals?.orders_count || 0}</p>
                      </div>
                      <div className="glass-card p-3">
                        <p className="text-xs text-gray-400">Receita</p>
                        <p className="text-lg md:text-xl font-bold text-green-400">{brl(waitersData.totals?.total_revenue)}</p>
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                      <select value={waiterFilter} onChange={(e) => setWaiterFilter(e.target.value)}
                        className="px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-orange-500"
                        data-testid="waiter-report-filter">
                        <option value="all">Todos os atendentes</option>
                        {waitersData.waiters.map((w, idx) => (
                          <option key={w.waiter_id || `n-${idx}`} value={w.waiter_id || w.waiter_name}>{w.waiter_name}</option>
                        ))}
                      </select>
                      <button
                        onClick={() => {
                          const rows = waitersData.waiters.flatMap((w) => (w.orders || []).map((o) => ({
                            Atendente: w.waiter_name, Mesa: o.table_number || o.table_id || '', 'Fechado em': o.closed_at || '',
                            Pagamento: o.payment_method || '', Subtotal: o.subtotal || 0, 'Taxa de serviço': o.service_fee || 0, Total: o.total || 0,
                          })));
                          exportToExcel(rows, 'fechamento_por_atendente', 'Atendentes');
                        }}
                        className="px-3 py-2 bg-green-600 hover:bg-green-700 rounded-lg text-white text-sm font-medium"
                        data-testid="waiter-report-export-xlsx">
                        Excel
                      </button>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {waitersData.waiters
                      .filter((w) => waiterFilter === 'all' || (w.waiter_id || w.waiter_name) === waiterFilter)
                      .map((w, idx) => {
                        const key = w.waiter_id || `n-${idx}`;
                        const isExpanded = expandedWaiter === key;
                        const initial = (w.waiter_name || '?').charAt(0).toUpperCase();
                        const maxRevenue = Math.max(...waitersData.waiters.map((x) => x.total_revenue || 0), 1);
                        const barPct = Math.max(3, Math.round(((w.total_revenue || 0) / maxRevenue) * 100));
                        return (
                          <div key={key} className="glass-card p-4 md:p-5" data-testid={`waiter-card-${idx}`}>
                            <button onClick={() => setExpandedWaiter(isExpanded ? null : key)} className="w-full flex items-center gap-3 md:gap-4 text-left">
                              <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-gradient-to-br from-orange-500 to-pink-500 flex items-center justify-center text-white font-bold text-base flex-shrink-0">{initial}</div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <h3 className="text-white font-semibold truncate">{w.waiter_name}</h3>
                                  <span className="text-xs px-2 py-0.5 rounded-full bg-orange-500/20 text-orange-300">#{idx + 1}</span>
                                </div>
                                <div className="mt-2 h-1.5 w-full bg-gray-800 rounded-full overflow-hidden">
                                  <div className="h-full bg-gradient-to-r from-orange-500 to-pink-500" style={{ width: `${barPct}%` }} />
                                </div>
                              </div>
                              <div className="text-right flex-shrink-0">
                                <p className="text-lg md:text-xl font-bold text-green-400">{brl(w.total_revenue)}</p>
                                <p className="text-xs text-gray-400">{w.orders_count} mesa{w.orders_count === 1 ? '' : 's'} · Ticket {brl(w.avg_ticket)}</p>
                              </div>
                            </button>
                            {isExpanded && (
                              <div className="mt-4 pt-4 border-t border-gray-800">
                                {Object.keys(w.payment_breakdown || {}).length > 0 && (
                                  <div className="mb-3 flex flex-wrap gap-2">
                                    {Object.entries(w.payment_breakdown).map(([m, v]) => (
                                      <span key={m} className="text-xs px-2 py-1 rounded-md bg-gray-800 text-gray-300 capitalize">{m}: <span className="text-green-400 font-medium">{brl(v)}</span></span>
                                    ))}
                                    <span className="text-xs px-2 py-1 rounded-md bg-gray-800 text-gray-300">Taxa de serviço: <span className="text-orange-400 font-medium">{brl(w.total_service_fee)}</span></span>
                                  </div>
                                )}
                                <div className="overflow-x-auto">
                                  <table className="w-full text-sm">
                                    <thead>
                                      <tr className="text-gray-400 text-xs border-b border-gray-800">
                                        <th className="text-left py-2">Mesa</th>
                                        <th className="text-left py-2">Fechado em</th>
                                        <th className="text-left py-2">Pagamento</th>
                                        <th className="text-right py-2">Taxa</th>
                                        <th className="text-right py-2">Total</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {(w.orders || []).map((o, i) => (
                                        <tr key={o.id || i} className="border-b border-gray-900 last:border-0">
                                          <td className="py-2 text-white">{o.table_number || '—'}</td>
                                          <td className="py-2 text-gray-300">{o.closed_at ? new Date(o.closed_at).toLocaleString('pt-BR') : '—'}</td>
                                          <td className="py-2 text-gray-300 capitalize">{o.payment_method || '—'}</td>
                                          <td className="py-2 text-right text-orange-300">{brl(o.service_fee)}</td>
                                          <td className="py-2 text-right text-green-400 font-semibold">{brl(o.total)}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}

              {/* Empty states */}
              {!loading && activeTab === 'overview' && !overview && (
                <div className="text-center py-12">
                  <TrendingUp className="w-12 h-12 md:w-16 md:h-16 text-gray-600 mx-auto mb-3" />
                  <p className="text-sm md:text-base text-gray-400">Nenhum dado disponível</p>
                  <p className="text-xs text-gray-500 mt-1">Selecione outro período</p>
                </div>
              )}
              {!loading && activeTab === 'team' && (!waitersData || (waitersData.waiters || []).length === 0) && (
                <div className="text-center py-12">
                  <Users className="w-12 h-12 md:w-16 md:h-16 text-gray-600 mx-auto mb-3" />
                  <p className="text-sm md:text-base text-gray-400">Nenhuma comanda fechada neste período</p>
                  <p className="text-xs text-gray-500 mt-1">Registre o atendente ao fechar as mesas para gerar histórico.</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportsContent;
